// Config generators for the relay + SDK bridge provisioning wizard.
//
// These are PURE string builders — no network, no secrets. The web app never
// transmits real controller passwords; every secret is emitted as the
// `__FILL_ON_PC__` placeholder that the on-site installer replaces by hand on
// the box. This mirrors the convention already used in the example files:
//   apps/hikvision-relay/.env.acuapue.example
//   apps/hikvision-sdk-bridge/appsettings.json
//
// Two event sources are supported, matching the two on-site topologies:
//   - "bridge":  SDK-only controllers (e.g. DS-K2812) reachable only over the
//                Hikvision SDK on :8000 — events/door commands go through the
//                local .NET bridge. Generates BOTH a relay .env and a bridge
//                appsettings.json.
//   - "direct":  controllers exposing ISAPI/HTTP (e.g. DS-K1T341CMF) — the
//                controller PUSHES events to the relay's :3900/event listener.
//                configure-device-push.js points it there; polling is only a
//                fallback (auto-disabled on push-only firmware). Generates ONLY
//                a relay .env (no bridge), plus the firewall + push-config steps.

export const SECRET_PLACEHOLDER = "__FILL_ON_PC__";

export type RelayEventSource = "bridge" | "direct";

export interface RelayConfig {
  /** Human label for the site header comment (e.g. "SOAPAP / Acuapue"). */
  siteName: string;
  /** LAN IP of the access controller. */
  controllerIp: string;
  /** SDK port for the bridge (usually 8000). Only used in "bridge" mode. */
  controllerPort: number;
  /** ISAPI/SDK username (non-secret; password stays on the PC). */
  username: string;
  /** Controller serial used as the bridge's fallback device serial. */
  deviceSerial: string;
  /** Cloud webhook URL (deployed hikvisionWebhook function). */
  webhookUrl: string;
  eventSource: RelayEventSource;
}

const stripTrailingSlash = (url: string): string => url.replace(/\/+$/, "");

/**
 * Build the relay agent's `.env`. Branches on `eventSource`:
 *  - bridge → adds EVENT_SOURCE=bridge + BRIDGE_URL + HEARTBEAT_INTERVAL_SECONDS
 *  - direct → plain ISAPI polling (CONTROLLER_IPS + POLL_INTERVAL_SECONDS)
 */
export function buildRelayEnv(cfg: RelayConfig): string {
  const webhook = stripTrailingSlash(cfg.webhookUrl || "");
  const lines: string[] = [
    "# ─────────────────────────────────────────────────────────────────────────────",
    `# Relay config — SITE: ${cfg.siteName || "(unnamed site)"}`,
    "#",
    "# Copy this file to `.env` ON THE ON-SITE PC and fill the __FILL_ON_PC__ values.",
    "# Do NOT commit the real .env — secrets stay on the PC only.",
    "# ─────────────────────────────────────────────────────────────────────────────",
    "",
    "# Port where this agent listens for ISAPI event pushes from the controller",
    "RELAY_PORT=3900",
    "",
    "# Cloud webhook URL (deployed Firebase function)",
    `WEBHOOK_URL=${webhook}`,
    "",
    "# Shared secret — must match ONE of the keys the cloud accepts. FILL ON THE PC.",
    "# The cloud's HIKVISION_WEBHOOK_API_KEY is a COMMA-SEPARATED LIST, so each site",
    "# may keep its own key: this value must appear in that list. Measured on",
    "# 22-sep-2026 in production, the installed relays do NOT share one key.",
    `WEBHOOK_API_KEY=${SECRET_PLACEHOLDER}`,
    "",
    "# ─── Optional 2nd/3rd cloud (multi-environment) ──────────────────────────────",
    "# The relay fans out to EVERY configured target: access events (history sync),",
    "# heartbeats, door-command results and card syncs all land in each cloud.",
    "# Leave unset to feed only WEBHOOK_URL above. To ALSO feed production, register",
    "# this device in the prod project FIRST, then uncomment and fill the prod key:",
    "# WEBHOOK_URL_2=https://us-central1-dumax-lite-production.cloudfunctions.net/hikvisionWebhook",
    `# WEBHOOK_API_KEY_2=${SECRET_PLACEHOLDER}`,
    "",
    "# ─── Hikvision Controller ────────────────────────────────────────────────────",
    "# LAN IP of the access controller.",
    `CONTROLLER_IPS=${cfg.controllerIp}`,
    "",
    "# ISAPI digest-auth credentials for the controller. FILL PASSWORD ON THE PC.",
    `CONTROLLER_USERNAME=${cfg.username || "admin"}`,
    `CONTROLLER_PASSWORD=${SECRET_PLACEHOLDER}`,
    "",
  ];

  if (cfg.eventSource === "bridge") {
    lines.push(
      "# ─── Event source: SDK bridge (controller has NO ISAPI/HTTP) ─────────────────",
      "# This controller only speaks the Hikvision SDK, so events and door commands",
      "# go through the local SDK bridge (apps/hikvision-sdk-bridge), not ISAPI.",
      "EVENT_SOURCE=bridge",
      "BRIDGE_URL=http://127.0.0.1:3901",
      "# BRIDGE_API_KEY=            # optional; must match the bridge's appsettings.json",
      "",
      "# Heartbeat liveness is read from the bridge's /status.",
      "HEARTBEAT_INTERVAL_SECONDS=30",
      "",
      "# (ISAPI poll interval — unused while EVENT_SOURCE=bridge)",
      "POLL_INTERVAL_SECONDS=5",
      "",
    );
  } else {
    lines.push(
      "# ─── Event source: ISAPI push (controller POSTs to :3900/event) ──────────────",
      "# Push is primary — run scripts/configure-device-push.js on the PC (see install",
      "# steps) to point the controller's HTTP-listening host at this relay. Polling is",
      "# only a fallback and is auto-disabled on push-only firmware (e.g. DS-K1T341CMF).",
      "POLL_INTERVAL_SECONDS=5",
      "",
      "# Heartbeat liveness via a direct ISAPI /ISAPI/System/deviceInfo probe (no bridge).",
      "HEARTBEAT_MODE=isapi",
      "HEARTBEAT_INTERVAL_SECONDS=30",
      "",
    );
  }

  lines.push(
    "# ─── Logging ─────────────────────────────────────────────────────────────────",
    "LOG_LEVEL=info",
    "LOG_DIR=./logs",
    "",
  );

  return lines.join("\n");
}

/**
 * Build the SDK bridge's `appsettings.json` (bridge mode only). Returns null
 * for direct mode, where no bridge is deployed.
 */
export function buildBridgeAppsettings(cfg: RelayConfig): string | null {
  if (cfg.eventSource !== "bridge") return null;
  const settings = {
    Controller: {
      Ip: cfg.controllerIp,
      Port: cfg.controllerPort || 8000,
      Username: cfg.username || "admin",
      Password: SECRET_PLACEHOLDER,
    },
    RelayEventUrl: "http://127.0.0.1:3900/event",
    BridgePort: 3901,
    BridgeApiKey: "",
    DeviceSerialFallback: cfg.deviceSerial || "",
    ReconnectSeconds: 10,
  };
  return JSON.stringify(settings, null, 2) + "\n";
}

/**
 * Build the copy/paste install commands the on-site installer runs. The bridge
 * steps are only included in "bridge" mode. Mirrors the real scripts:
 *   apps/hikvision-relay/scripts/install-service.js  (node-windows service)
 *   apps/hikvision-sdk-bridge/scripts/install-service.ps1  (WinSW service)
 */
/**
 * La URL de `hikvisionWebhook` del MISMO proyecto y región que la API.
 *
 * Se deriva de `VITE_FIREBASE_API_URL` cambiando sólo el nombre de la función
 * (último segmento del path): `…/torreDeControlApi` → `…/hikvisionWebhook`, y
 * en el emulador `…/us-central1/torreDeControlApi` → `…/us-central1/hikvisionWebhook`.
 *
 * Se parsea como URL en vez de con un regex sobre el string: el regex que
 * había (`api.replace(/\/[^/]+$/, "/hikvisionWebhook")`) con una API sin path
 * (`https://host`) SÍ matchea —se come el host— y devolvía
 * `https://hikvisionWebhook`, una URL que parece válida y apunta a ningún lado.
 * Sin path no hay nombre de función que reemplazar: se devuelve vacío, y el
 * campo obligatorio obliga a pegar la correcta.
 */
export function webhookUrlFromApiUrl(apiUrl: string | undefined | null): string {
  const raw = (apiUrl || "").trim().replace(/\/+$/, "");
  if (!raw) return "";
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return "";
  }
  const segments = url.pathname.split("/").filter(Boolean);
  if (segments.length === 0) return "";
  segments[segments.length - 1] = "hikvisionWebhook";
  return `${url.origin}/${segments.join("/")}`;
}

/** Nombre del servicio del relay en el SCM (lo registra node-windows). */
export const RELAY_SERVICE_SCM_NAME = "hikvisionrelayagent.exe";
/** Nombre del servicio del bridge del SDK (lo registra WinSW). */
export const BRIDGE_SERVICE_NAME = "HikSdkBridge";

export function buildInstallCommands(cfg: RelayConfig): string {
  const lines: string[] = [
    "# Run these on the on-site Windows PC. Elevated (Administrator) PowerShell.",
    "",
    "# 1) Relay agent (Node) — from apps/hikvision-relay",
    "npm install",
    "#    -> place the generated .env in this folder, then fill __FILL_ON_PC__ secrets",
    "npm run install-service      # installs the relay Windows service (SCM name: hikvisionrelayagent.exe)",
  ];

  if (cfg.eventSource === "bridge") {
    lines.push(
      "",
      "# 2) SDK bridge (.NET) — from apps/hikvision-sdk-bridge",
      "dotnet publish -c Release -o publish",
      "#    -> place the generated appsettings.json in publish\\, then fill the password",
      "powershell -ExecutionPolicy Bypass -File scripts\\install-service.ps1",
    );
  } else {
    lines.push(
      "",
      "# 2) Open the relay's push listener to the LAN. Push terminals POST events to",
      "#    this PC on RELAY_PORT; Windows Firewall blocks inbound by default.",
      'New-NetFirewallRule -DisplayName "Hikvision Relay 3900 inbound" -Direction Inbound -Protocol TCP -LocalPort 3900 -Action Allow -Profile Any',
      "",
      "# 3) Tell the controller to push events to this relay (one-shot). Reads",
      "#    CONTROLLER_IPS/USERNAME/PASSWORD from .env; --ip is THIS PC's LAN IP.",
      "node scripts/configure-device-push.js --ip <THIS_PC_LAN_IP>",
    );
  }

  lines.push(
    "",
    // En modo direct hay un solo servicio: "both" mandaba a buscar un segundo
    // RUNNING que no existe.
    cfg.eventSource === "bridge"
      ? "# Verify (both must say RUNNING):"
      : "# Verify (must say RUNNING):",
    // `sc.exe` y no `sc`: en Windows PowerShell 5.1 `sc` es alias de
    // Set-Content, así que `sc query X` escribe un archivo llamado `query` y no
    // imprime nada — el operador no ve ni RUNNING ni error.
    //
    // El nombre en el SCM que registra node-windows es `hikvisionrelayagent.exe`.
    // `HikvisionRelayAgent` es sólo el DisplayName de services.msc, y
    // `sc.exe query HikvisionRelayAgent` devuelve ERROR 1060 ("el servicio no
    // existe") con el servicio instalado y corriendo: el operador concluía que
    // la instalación había fallado.
    `sc.exe query ${RELAY_SERVICE_SCM_NAME}`,
  );
  if (cfg.eventSource === "bridge") {
    lines.push(`sc.exe query ${BRIDGE_SERVICE_NAME}`);
  }

  if (cfg.eventSource !== "bridge") {
    lines.push(
      "",
      "# IMPORTANT — register this reader as a Control Point with an ip_address that",
      "# MATCHES the controller's real LAN IP: push events carry no serial, so the",
      "# webhook resolves the device by IP. Give the reader a STATIC IP / DHCP",
      "# reservation so it can't drift off the registered address.",
    );
  }

  return lines.join("\n");
}
